import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { CloseOutlined } from '@ant-design/icons';
import { useUser } from '../../context/UserContext';
import Message from '../Message';
import { checkSensitiveWords } from '../../utils/sensitiveWords';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const MessageContainer = styled.div.attrs({ id: 'name-modal-message' })`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
  display: flex;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 24px;
  width: 510px;
  height: 225px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    height: auto;
    margin: 16px;
    padding: 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  font-size: 18px;
  
  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.75)'};
  }
`;

const Title = styled.h2`
  margin: 0 0 32px 0;
  color: ${({ theme }) => theme.text};
  font-size: 16px;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  background: #E8E8E8;
  padding: 4px 11px;
  color: #000000;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: #888888;
  }

  &:focus {
    outline: none;
    border-color: #FF4D4F;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
    background: #E8E8E8 !important;
  }

  /* 处理浏览器自动填充的背景色 - 强化版 */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
    box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
    -webkit-text-fill-color: #000000 !important;
    caret-color: #000000;
    transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
    background-color: #E8E8E8 !important;
  }
  
  /* 添加更强的CSS规则覆盖Chrome的自动填充样式 */
  @-webkit-keyframes autofill {
    to {
      color: #000000;
      background: #E8E8E8;
    }
  }
  
  &:-webkit-autofill {
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }
  
  /* 额外的CSS规则来防止浏览器自动填充样式 */
  &:-internal-autofill-selected {
    background-color: #E8E8E8 !important;
    background-image: none !important;
    color: #000000 !important;
  }
  
  /* 通用样式覆盖 */
  @-webkit-keyframes onAutoFillStart { from {} to {}}
  @keyframes onAutoFillStart { from {} to {}}
  
  &:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-fill-mode: both;
  }
  
  &:autofill {
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 32px;
`;

const Button = styled.button`
  height: 32px;
  padding: 4px 15px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CancelButton = styled(Button)`
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#FFFFFF'};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#D9D9D9'};
  color: ${({ theme }) => theme.text};
  
  &:hover {
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.25)' : '#D9D9D9'};
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#F0F0F0'};
  }
`;

const SubmitButton = styled(Button)`
  background: #FF4D4F;
  border: 1px solid #FF4D4F;
  color: white;
  
  &:hover {
    background: #E53E3E;
    border-color: #E53E3E;
  }
`;

const UpdateNameModal = ({ onClose }) => {
  const { user } = useAuth();
  const { updateUserAndToken } = useUser();
  const [userName, setUserName] = useState(user?.userName || '');
  const [loading, setLoading] = useState(false);
  const [foundSensitiveWords, setFoundSensitiveWords] = useState([]);

  const checkForSensitiveContent = (text) => {
    const found = checkSensitiveWords(text);
    setFoundSensitiveWords(found);
    return found.length > 0;
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setUserName(newValue);
    const hasSensitiveWords = checkForSensitiveContent(newValue);
    
    if (hasSensitiveWords) {
      Message.error('检测到敏感词，请重新输入', 'name-modal-message');
      setUserName(''); // 清除输入内容
      setFoundSensitiveWords([]); // 重置敏感词状态
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 检查敏感词
    if (checkForSensitiveContent(userName)) {
      Message.error('检测到敏感词，请重新输入', 'name-modal-message');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(
        '//server.battleverse.cn/user/update/name.do',
        { userName },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        updateUserAndToken(response.data.data.user, response.data.data.token);
        Message.success('昵称修改成功', 'name-modal-message');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        Message.error(response.data.msg || '修改昵称失败', 'name-modal-message');
      }
    } catch (err) {
      Message.error('修改昵称失败，请稍后重试', 'name-modal-message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <MessageContainer />
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <CloseOutlined />
        </CloseButton>
        <Title>修改用户名</Title>
        <Form onSubmit={handleSubmit}>
          <StyledInput
            type="text"
            value={userName}
            $value={userName}
            onChange={handleInputChange}
            required
          />
          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>
              取消
            </CancelButton>
            <SubmitButton
              type="submit"
              disabled={loading}
            >
              确认
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UpdateNameModal;
